/**
 * Is the table socket still there? Discord proxies the Activity's WebSocket, and a
 * proxied socket can die without a close event: the browser still reports it open,
 * the last snapshot freezes on screen and every move goes nowhere. Silence is the
 * only sign, so the client prods the server and gives up when nothing answers.
 * Pure: time comes in as a parameter.
 */

/** Silence this long and we prod the server; also keeps an idle proxy from dropping us. */
export const QUIET_MS = 15_000;

/** No answer this long after the prod: the socket is gone, whatever the browser says. */
export const DEAD_AFTER_MS = 10_000;

/** How often the verdict is asked for; fine enough for both times above. */
export const CHECK_EVERY_MS = 5_000;

/**
 * A lost socket covers the table only after this (review 0005 S1). A table that
 * closed normally reconnects at once; veiling the game for that blink is noise.
 */
export const VEIL_AFTER_MS = 2_500;

export interface Liveness {
  /** When the server was last heard from; any message counts, `pong` included. */
  readonly lastHeardAt: number;
  /** When the unanswered prod went out; null while nothing is outstanding. */
  readonly pingedAt: number | null;
}

export type LivenessAction = "wait" | "ping" | "dead";

/** Anything arrived: the socket is alive and no prod is outstanding. */
export function heard(state: Liveness, now: number): Liveness {
  return { ...state, lastHeardAt: now, pingedAt: null };
}

/** A prod went out; the answer is now what we are waiting for. */
export function pinged(state: Liveness, now: number): Liveness {
  return { ...state, pingedAt: now };
}

/**
 * A quiet table is not a dead one: a relaxed bid takes 75 s and the table says
 * nothing meanwhile. Only an unanswered prod proves the socket is gone.
 */
export function nextAction(state: Liveness, now: number): LivenessAction {
  if (state.pingedAt === null) return now - state.lastHeardAt < QUIET_MS ? "wait" : "ping";
  return now - state.pingedAt < DEAD_AFTER_MS ? "wait" : "dead";
}

/** Is the reconnect veil due? `since` is when the connection last changed. */
export function veilVisible(open: boolean, since: number, now: number): boolean {
  return !open && now - since >= VEIL_AFTER_MS;
}
